import type { Type, Input, InputSignal } from '@angular/core';

import type { Observable } from 'rxjs';

import type { Expand, UnionToIntersection } from './types';

type NgInputDef = Input & { name: string };

export type NgInput = NgInputDef | string;
export type NgInputs = NgInput[];

type NormalizedNgInput<
  Name extends string = string,
  Alias extends string | undefined = undefined,
  Required extends boolean | undefined = undefined,
  Transform extends ((value: any) => any) | undefined = undefined,
> = {
  name: Name;
  alias: Alias;
  required: Required;
  transform: Transform;
};

type NormalizeInput<Input extends NgInput> = Input extends string
  ? NormalizedNgInput<Input>
  : Input extends NgInputDef
    ? NormalizedNgInput<
        Input['name'],
        Input['alias'] extends string ? Input['alias'] : undefined,
        Input['required'] extends boolean ? Input['required'] : false,
        Input['transform'] extends (val: any) => any ? Input['transform'] : undefined
      >
    : never;

type InputKey<Name extends string, Alias extends string | undefined> = Alias extends undefined
  ? Name
  : Alias;

type InputValue<Value, Transform extends undefined | ((value: any) => any)> = Transform extends (
  value: any,
) => any
  ? Parameters<Transform>[0]
  : Value extends InputSignal<infer R>
    ? R
    : Value;

type GetOptional<A, B> = {
  [K in keyof A & keyof B]: A[K] extends B[K] ? never : K;
};

type OptionalKeys<T> = GetOptional<T, Required<T>>[keyof T];

export type StaticInputsComponent = Type<any> & { NgInputs: NgInputs; NgOutputs?: string[] };
export type GetInputs<
  Cmp extends StaticInputsComponent,
  Input = NormalizeInput<Cmp['NgInputs'][number]>,
> = Expand<
  UnionToIntersection<
    Input extends NormalizedNgInput<infer Name, infer Alias, infer _Required, infer Transform>
      ? InputKey<Name, Alias> extends OptionalKeys<InstanceType<Cmp>>
        ? {
            [k in InputKey<Name, Alias>]?: InputValue<InstanceType<Cmp>[Name], Transform>;
          }
        : {
            [k in InputKey<Name, Alias>]: InputValue<InstanceType<Cmp>[Name], Transform>;
          }
      : never
  >
>;

export type GetOutputs<
  Cmp extends StaticInputsComponent,
  Output = Cmp['NgOutputs'] extends string[] ? Cmp['NgOutputs'][number] : never,
> = Expand<
  UnionToIntersection<
    Output extends string
      ? {
          [k in Output]: InstanceType<Cmp>[Output] extends Observable<infer T>
            ? (arg: T) => void
            : never;
        }
      : never
  >
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const uniq = Symbol('RtComponentOutletConfig');
export interface RtComponentOutletConfig<Cmp extends StaticInputsComponent> {
  __type: typeof uniq;
  component: Cmp;
  inputs: GetInputs<Cmp>;
  outputs?: GetOutputs<Cmp>;
}

export const componentOutletConfigFactory = <Cmp extends StaticInputsComponent>(
  component: Cmp,
  options: {
    inputs: GetInputs<Cmp>;
    outputs?: GetOutputs<Cmp>;
  },
) => <RtComponentOutletConfig<Cmp>>{ component, ...options };
